import React, { JSX, useCallback, useMemo } from 'react';
import { Column } from 'react-table';
import { Badge, Button, Flex, Text, useToast } from '@chakra-ui/react';
import { Modal, ModalType, Table } from 'common/components';
import { useTranslations } from 'contexts/LocalizationContext';

import { UserToken } from '../models/application-user';

interface TokensModalProps {
  isModalVisible: boolean;
  onModalClose: () => void;
  tokens: UserToken[];
  userId: string;
}
export const isTokenActive = (expiresAt: Date): boolean => {
  const now = new Date();
  return now < new Date(expiresAt);
};

export const TokensModal = ({ isModalVisible, onModalClose, tokens, userId }: TokensModalProps): JSX.Element => {
  const toast = useToast();
  const translations = useTranslations();

  const handleCopyToken = useCallback(
    (token: string) => {
      navigator.clipboard.writeText(token).then(() => {
        toast({
          title: translations('tokens_modal_token_copied'),
          description: translations('tokens_modal_token_copied_description'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      });
    },
    [toast, translations],
  );

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const columns: Column<UserToken>[] = useMemo(
    () => [
      {
        Header: translations('tokens_table_token'),
        accessor: 'token',
        Cell: ({ value }: { value: string }) => (
          <Text fontFamily="mono" fontSize="sm" wordBreak="break-all" onClick={() => handleCopyToken(value)}>
            {value}
          </Text>
        ),
      },
      {
        Header: translations('tokens_table_created'),
        accessor: 'createdAt',
        Cell: ({ value }: { value: Date }) => <Text fontSize="sm">{formatDate(value)}</Text>,
      },
      {
        Header: translations('tokens_table_expires'),
        accessor: 'expiresAt',
        Cell: ({ value }: { value: Date }) => <Text fontSize="sm">{formatDate(value)}</Text>,
      },
      {
        Header: translations('tokens_table_status'),
        id: 'status',
        accessor: 'expiresAt',
        Cell: ({ value }: { value: Date }) => {
          const active = isTokenActive(value);
          return (
            <Badge colorScheme={active ? 'green' : 'red'}>
              {active ? translations('tokens_table_active') : translations('tokens_table_expired')}
            </Badge>
          );
        },
      },
    ],
    [handleCopyToken, translations],
  );

  return (
    <Modal
      isModalVisible={isModalVisible}
      onModalClose={onModalClose}
      modalHeader={`${translations('tokens_modal_header')} - ${userId}`}
      type={ModalType.INFO}
      customModalFooter={
        <Flex justifyContent="flex-end" p={4}>
          <Button onClick={onModalClose}>{translations('tokens_modal_close')}</Button>
        </Flex>
      }
      size="lg"
    >
      {tokens.length === 0 ? (
        <Text>{translations('tokens_modal_no_tokens')}</Text>
      ) : (
        <Table<UserToken>
          data={tokens}
          columns={columns}
          isLoading={false}
          searchEnabled={false}
          paginationEnabled={false}
        />
      )}
    </Modal>
  );
};
